import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  ProjectErrors,
  TProjectDataWithoutRepository,
  initialProjectData
} from "@/zapi-frontend/widgets/CreateProjectForm";
import { TModelType } from "@/zapi-frontend/shared/types/models";
import { useState } from "react";
import { LS_CREATE_PROJECT_STORAGE } from "../shared/local-storage/keys";

export type TBusinessModel = {
  id?: number;
  modelArchitecture: string;
  productType: string;
  transactionModel: string;
  customerType: string;
  industrySegment?: string;
  textualDescription: string;
  confidence?: number;
};

export type TDocumentSelection = {
  documentId: number;
  selected: boolean;
  contentType: "FULL" | "SUMMARIZED";
};
type TCreateProjectState = {
  projectData: TProjectDataWithoutRepository;
  createdProjectId: number | null;
  prefillOptions: { model: TModelType | null };
  shouldPrefill: boolean;
  errors: ProjectErrors;
  isLoading: boolean;
  processingCompleted: boolean;
  documentSelections: TDocumentSelection[];

  setProjectData: (data: TProjectDataWithoutRepository) => void;
  updateProjectData: (updates: Partial<TProjectDataWithoutRepository>) => void;
  setCreatedProjectId: (id: number | null) => void;
  setPrefillOptions: (options: { model: TModelType | null }) => void;
  setShouldPrefill: (shouldPrefill: boolean) => void;
  setErrors: (errors: ProjectErrors) => void;
  updateErrors: (updates: Partial<ProjectErrors>) => void;
  setIsLoading: (isLoading: boolean) => void;
  setProcessingCompleted: (val: boolean) => void;
  setDocumentSelections: (selections: TDocumentSelection[]) => void;
  clearForm: () => void;
  reset: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { repository, ...initialProjectDataWithoutRepository } = initialProjectData;

export const useCreateProjectStore = create<TCreateProjectState>()(
  persist(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (set, get) => ({
      projectData: initialProjectDataWithoutRepository,
      createdProjectId: null,
      logo: null,
      prefillOptions: { model: "gpt-4.1" },
      shouldPrefill: false,
      errors: {},
      isLoading: false,
      processingCompleted: false,
      documentSelections: [],

      setProjectData: (data) => set({ projectData: data }),

      updateProjectData: (updates) =>
        set((state) => ({
          projectData: { ...state.projectData, ...updates }
        })),

      setCreatedProjectId: (id) => set({ createdProjectId: id }),

      setPrefillOptions: (options) => set({ prefillOptions: options }),

      setShouldPrefill: (shouldPrefill) => set({ shouldPrefill }),

      setErrors: (errors) => set({ errors }),

      updateErrors: (updates) =>
        set((state) => ({
          errors: { ...state.errors, ...updates }
        })),

      setIsLoading: (isLoading) => set({ isLoading }),

      setProcessingCompleted: (val) => set({ processingCompleted: val }),

      setDocumentSelections: (selections) => set({ documentSelections: selections }),

      clearForm: () =>
        set({
          projectData: initialProjectDataWithoutRepository,
          createdProjectId: null,
          prefillOptions: { model: "gpt-4.1" },
          shouldPrefill: false,
          errors: {},
          isLoading: false,
          processingCompleted: false,
          documentSelections: []
        }),

      reset: () =>
        set({
          projectData: initialProjectDataWithoutRepository,
          createdProjectId: null,
          prefillOptions: {
            model: "gpt-4.1"
          },
          shouldPrefill: false,
          errors: {},
          isLoading: false,
          processingCompleted: false,
          documentSelections: []
        })
    }),
    {
      name: LS_CREATE_PROJECT_STORAGE,
      partialize: (state) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isLoading, ...rest } = state;
        return rest;
      }
    }
  )
);

export const useCreateProjectFiles = () => {
  const [files, setFiles] = useState<File[]>([]);

  return {
    files,
    setFiles,
    addFiles: (newFiles: File[]) => setFiles((prev) => [...prev, ...newFiles]),
    removeFile: (fileToRemove: File) => setFiles((prev) => prev.filter((f) => f !== fileToRemove)),
    clearFiles: () => setFiles([])
  };
};
